import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

function latestCachedEventTime(queryClient: ReturnType<typeof useQueryClient>) {
  return queryClient
    .getQueryCache()
    .findAll({ queryKey: ['events'] })
    .reduce(
      (latest, query) => Math.max(latest, query.state.dataUpdatedAt),
      0
    );
}

function formatAge(updatedAt: number): string {
  if (!updatedAt) return 'No calendar data cached';

  const minutes = Math.max(
    1,
    Math.floor((Date.now() - updatedAt) / 60_000)
  );
  if (minutes < 60) return `Cached ${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `Cached ${hours}h ago`;

  return `Cached ${Math.floor(hours / 24)}d ago`;
}

export default function OfflineStatus() {
  const queryClient = useQueryClient();
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [updatedAt, setUpdatedAt] = useState(() =>
    latestCachedEventTime(queryClient)
  );

  useEffect(() => {
    const refreshCacheTime = () =>
      setUpdatedAt(latestCachedEventTime(queryClient));
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => {
      refreshCacheTime();
      setIsOnline(false);
    };
    const unsubscribe = queryClient
      .getQueryCache()
      .subscribe(refreshCacheTime);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [queryClient]);

  if (isOnline) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 flex justify-center pointer-events-none px-3 pb-3"
      role="status"
    >
      <span className="rounded-full bg-ink-800 px-3 py-1.5 font-mono text-xs text-paper-50 shadow-lg">
        Offline — {formatAge(updatedAt)}
      </span>
    </div>
  );
}
